package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * 统一状态层合成器。
 *
 * 状态优先级固定为 pressed > focused > hovered。dragged token 当前没有稳定事件源，
 * 仅在主题中预留，合成器暂不消费。
 */
@Suppress("LongParameterList", "MagicNumber")
object StateLayerCompositor {

    /** 在元素完整 box 上叠加当前最高优先级状态层。 */
    @JvmStatic
    fun overlay(
        bridge: UiRenderBridge,
        element: UIElement<*>?,
        box: Box,
        theme: ControlTheme?,
        disabled: Boolean,
        overrides: Overrides?,
        includeFocus: Boolean,
    ): Boolean = overlay(
        bridge, element, box.x(), box.y(), box.width(), box.height(),
        theme, disabled, overrides, includeFocus,
    )

    /** 在自定义矩形区域上叠加当前最高优先级状态层。 */
    @JvmStatic
    fun overlay(
        bridge: UiRenderBridge,
        element: UIElement<*>?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        theme: ControlTheme?,
        disabled: Boolean,
        overrides: Overrides?,
        includeFocus: Boolean,
    ): Boolean {
        val color = resolveOverlayColor(element, theme, disabled, overrides, includeFocus)
        if (color == 0) {
            return false
        }
        bridge.fillRect(x, y, width, height, color)
        return true
    }

    /** 在圆角区域上叠加当前最高优先级状态层。 */
    @JvmStatic
    fun overlayRounded(
        bridge: UiRenderBridge,
        element: UIElement<*>?,
        box: Box,
        cornerRadius: Float,
        theme: ControlTheme?,
        disabled: Boolean,
        overrides: Overrides?,
        includeFocus: Boolean,
    ): Boolean = overlayRounded(
        bridge,
        element,
        box,
        CornerRadii.uniform(cornerRadius),
        theme,
        disabled,
        overrides,
        includeFocus,
    )

    /** 在任意角圆角区域上叠加当前最高优先级状态层。 */
    @JvmStatic
    fun overlayRounded(
        bridge: UiRenderBridge?,
        element: UIElement<*>?,
        box: Box?,
        cornerRadii: CornerRadii?,
        theme: ControlTheme?,
        disabled: Boolean,
        overrides: Overrides?,
        includeFocus: Boolean,
    ): Boolean {
        if (bridge == null || box == null || cornerRadii == null) {
            return false
        }
        if (cornerRadii.isZero()) {
            return false
        }
        val color = resolveOverlayColor(element, theme, disabled, overrides, includeFocus)
        if (color == 0) {
            return false
        }
        val fill = StyledFill.builder()
            .cornerRadii(cornerRadii.toArray())
            .borderWidth(0f)
            .borderColor(0)
            .build()
        val batch = RenderBatch(
            RenderCommandType.ROUNDED_RECT,
            null,
            RenderCommand.NO_CLIP,
            listOf(
                RenderCommand.roundedRect(
                    box.x(),
                    box.y(),
                    box.width(),
                    box.height(),
                    color,
                    RenderCommand.NO_CLIP,
                    fill,
                ),
            ),
        )
        bridge.submitBatch(batch)
        return true
    }

    private fun resolveOverlayColor(
        element: UIElement<*>?,
        theme: ControlTheme?,
        disabled: Boolean,
        overrides: Overrides?,
        includeFocus: Boolean,
    ): Int {
        if (disabled || element == null || theme == null) {
            return 0
        }
        val state = activeState(element, includeFocus) ?: return 0
        val actualOverrides = overrides ?: Overrides.none()
        return StateLayer.withAlpha(actualOverrides.color(state, theme), actualOverrides.opacity(state, theme))
    }

    /** Button 的禁用规则集中在这里，避免各渲染器重复判断。 */
    @JvmStatic
    fun isDisabled(element: UIElement<*>?): Boolean = isLeaveBehindButtonDisabled(element)

    /** 创建逐状态颜色和透明度覆盖；null 表示回退主题默认。 */
    @JvmStatic
    fun overrides(
        hoveredColor: Int?,
        hoveredOpacity: Float?,
        focusedColor: Int?,
        focusedOpacity: Float?,
        pressedColor: Int?,
        pressedOpacity: Float?,
    ): Overrides = Overrides(
        hoveredColor,
        hoveredOpacity,
        focusedColor,
        focusedOpacity,
        pressedColor,
        pressedOpacity,
    )

    /** 为三种状态使用同一覆盖色，透明度仍走主题默认。 */
    @JvmStatic
    fun colorOverride(color: Int): Overrides = overrides(color, null, color, null, color, null)

    private fun activeState(element: UIElement<*>, includeFocus: Boolean): State? = when {
        element.pressed() -> State.PRESSED
        includeFocus && element.focused() -> State.FOCUSED
        element.hovered() -> State.HOVERED
        else -> null
    }

    internal enum class State {
        HOVERED,
        FOCUSED,
        PRESSED,
    }

    /** 每种状态可独立覆盖颜色与透明度，未覆盖时走主题默认 token。 */
    class Overrides internal constructor(
        private val hoveredColor: Int?,
        private val hoveredOpacity: Float?,
        private val focusedColor: Int?,
        private val focusedOpacity: Float?,
        private val pressedColor: Int?,
        private val pressedOpacity: Float?,
    ) {

        internal fun color(state: State, theme: ControlTheme): Int {
            val value = when (state) {
                State.PRESSED -> pressedColor
                State.FOCUSED -> focusedColor
                State.HOVERED -> hoveredColor
            }
            return value ?: theme.onSurface
        }

        internal fun opacity(state: State, theme: ControlTheme): Float {
            val value = when (state) {
                State.PRESSED -> pressedOpacity
                State.FOCUSED -> focusedOpacity
                State.HOVERED -> hoveredOpacity
            }
            return value ?: defaultOpacity(state, theme)
        }

        private fun defaultOpacity(state: State, theme: ControlTheme): Float = when (state) {
            State.PRESSED -> theme.stateLayerPressedOpacity
            State.FOCUSED -> theme.stateLayerFocusOpacity
            State.HOVERED -> theme.stateLayerHoverOpacity
        }

        companion object {
            private val NONE = Overrides(null, null, null, null, null, null)

            /** 无覆盖时使用主题 onSurface 与对应状态透明度。 */
            @JvmStatic
            fun none(): Overrides = NONE
        }
    }
}
